import React from 'react';
import { Text, Skeleton, SkeletonItem } from '@fluentui/react-components';
import {
  LocationRegular,
  CalendarLtrRegular,
  DocumentRegular,
  SettingsRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ReviewSection } from '@layouts/ReviewCard';
import { CaterersPanel } from '@components/common/CaterersPanel';
import { FilmingApprovalPanel } from '@components/common/FilmingApprovalPanel';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { useReviewSummaryStyles } from './ReviewSummary.styles';
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage';
import { SpaceImageFallback } from '@components/spaces/SpaceImageFallback';
import { VISIT_PURPOSE_OPTIONS } from '@config/bookingFormConfig';
import { formatDate, formatTime12Hour } from '@/utils/dateFormat';
import { formatUtcDateInTimeZone, formatUtcTimeInTimeZone } from '@/utils/timeZone';
import type { SpaceDetails, StepCompletionMap, ReviewSectionState } from '@store/models/booking';
import type { Building } from '@store/models/buildings/entities';
import type { OnSiteContact } from '@store/models/events/entities';

function getSectionState(
  stepIndex: number,
  highestVisitedStep: number,
  stepCompletionMap?: StepCompletionMap
): ReviewSectionState {
  if (stepIndex > highestVisitedStep + 1) return 'locked';
  if (stepIndex === highestVisitedStep + 1) return 'actionable';
  if (stepCompletionMap && !stepCompletionMap.stepResults[stepIndex]?.isComplete)
    return 'actionable';
  return 'completed';
}

export interface ReviewSummaryProps {
  formData: Record<string, unknown>;
  selectedSpace: SpaceDetails | null;
  selectedBuilding?: Building | null;
  onEditStep: (stepIndex: number) => void;
  highestVisitedStep?: number;
  stepCompletionMap?: StepCompletionMap;
  buildingLoading?: boolean;
  spacesLoading?: boolean;
}

export const ReviewSummary: React.FC<ReviewSummaryProps> = ({
  formData,
  selectedSpace,
  selectedBuilding,
  onEditStep,
  highestVisitedStep = 4,
  stepCompletionMap,
  buildingLoading = false,
  spacesLoading = false,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useReviewSummaryStyles();

  // Merge space details from formData (always has full object) with selectedSpace prop
  const formSpace = formData.selectedSpace as SpaceDetails | undefined;
  const space: SpaceDetails | null = selectedSpace
    ? {
        ...selectedSpace,
        imageUrl: selectedSpace.imageUrl ?? formSpace?.imageUrl,
        floor: selectedSpace.floor ?? formSpace?.floor,
        roomNumber: selectedSpace.roomNumber ?? formSpace?.roomNumber,
        roomCapacity: selectedSpace.roomCapacity ?? formSpace?.roomCapacity,
      }
    : (formSpace ?? null);

  const {
    src: spaceImageSrc,
    isLoading: isImageLoading,
    isError: isImageError,
  } = useAuthenticatedImage(space?.imageUrl, '');
  const showFallback = !space?.imageUrl || isImageError;

  const val = (key: string) => (formData[key] as string) ?? '—';
  const yesNo = (key: string) =>
    formData[key] === 'yes' ? t('bookingForm.options.yes') : t('bookingForm.options.no');

  const visitPurposeLabel = (() => {
    const code = formData.visitPurposeCode as string | undefined;
    if (!code) return '—';
    const match = VISIT_PURPOSE_OPTIONS.find((option) => option.value === code);
    return match?.labelKey ?? code;
  })();
  const showBusinessGroup = selectedBuilding?.businessGroupConfig?.required === true;
  const businessGroupLabel = (() => {
    const code = formData.businessGroupCode as string | undefined;
    if (!code) return '—';
    const match = selectedBuilding?.businessGroupConfig?.businessGroups?.find(
      (group) => group.code === code
    );
    return match?.label ?? code;
  })();

  // Resolve room configuration codes to display labels
  const configurationLabel = (() => {
    const setups = formData.selectedSetups as string[] | undefined;
    if (!setups || setups.length === 0) return '—';
    return setups.map((code) => getRoomLayoutMeta(code).title).join(', ');
  })();

  const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
  const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;

  const contacts = (formData.onSiteContacts as OnSiteContact[] | undefined) ?? [];

  const buildAddress = () => {
    const parts: string[] = [];
    if (selectedBuilding?.buildingName) parts.push(selectedBuilding.buildingName);
    if (space?.floor) parts.push(`Floor ${space.floor}`);
    if (selectedBuilding?.buildingAddress) parts.push(selectedBuilding.buildingAddress);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const formatDateTime = () => {
    const utcStart = formData.eventStartDate as string | undefined;
    const utcEnd = formData.eventEndDate as string | undefined;
    const timeZone = selectedBuilding?.gmtZone;
    const timeZoneShort = selectedBuilding?.GMTZoneShort;

    if (utcStart && utcEnd) {
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      };

      const startDate = formatUtcDateInTimeZone(utcStart, timeZone);
      const endDate = formatUtcDateInTimeZone(utcEnd, timeZone);
      const startTime = formatUtcTimeInTimeZone(utcStart, 'en-US', timeZone, timeOptions);
      const endTime = formatUtcTimeInTimeZone(utcEnd, 'en-US', timeZone, timeOptions);
      const tz = timeZoneShort ? ` ${timeZoneShort}` : '';

      const datePart = startDate === endDate ? startDate : `${startDate} to ${endDate}`;
      return `${datePart} | ${startTime} to ${endTime}${tz}`;
    }

    if (!dateRange?.start) return '—';

    const fallbackDate = (value: string) => {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return formatDate(d);
    };

    let result = fallbackDate(dateRange.start);
    if (dateRange.end && dateRange.end !== dateRange.start) {
      result += ` to ${fallbackDate(dateRange.end)}`;
    }
    if (timeRange?.start) {
      result += `  |  ${formatTime12Hour(timeRange.start)}`;
      if (timeRange.end) result += ` to ${formatTime12Hour(timeRange.end)}`;
    }
    return result;
  };

  return (
    <div className={styles.root}>
      {/* 1. Selected Space */}
      <ReviewSection
        title={t('bookingForm.review.selectedSpace')}
        icon={<LocationRegular />}
        onEdit={() => onEditStep(1)}
        sectionState={getSectionState(1, highestVisitedStep, stepCompletionMap)}
        loading={spacesLoading}
      >
        <div className={styles.spaceCard}>
          {isImageLoading && space?.imageUrl ? (
            <Skeleton aria-label="Loading image">
              <SkeletonItem className={styles.spaceImage} />
            </Skeleton>
          ) : showFallback ? (
            <SpaceImageFallback className={styles.spaceImage} />
          ) : (
            <img src={spaceImageSrc} alt={space?.name ?? ''} className={styles.spaceImage} />
          )}
          <div className={styles.spaceInfo}>
            <Text className={styles.spaceName}>
              {space?.name ?? t('bookingForm.review.notYetCompleted')}
            </Text>
            <Text className={styles.spaceDetail}>
              <span className={styles.spaceDetailLabel}>{t('bookingForm.review.address')}:</span>{' '}
              {buildAddress()}
            </Text>
            <Text className={styles.spaceDetail}>
              <span className={styles.spaceDetailLabel}>{t('bookingForm.review.capacity')}:</span>{' '}
              {space?.roomCapacity ?? '—'}
            </Text>
            <Text className={styles.spaceDetail}>
              <span className={styles.spaceDetailLabel}>
                {t('bookingForm.review.configuration')}:
              </span>{' '}
              {configurationLabel}
            </Text>
          </div>
        </div>
      </ReviewSection>

      {/* 2. When and where */}
      <ReviewSection
        title={t('bookingForm.review.whenAndWhereTitle')}
        icon={<CalendarLtrRegular />}
        columns={2}
        items={[
          {
            label: t('bookingForm.review.stateProvince'),
            value: selectedBuilding?.stateName || (formData.stateProvince as string) || '—',
          },
          {
            label: t('bookingForm.review.dateAndTime'),
            value: formatDateTime(),
          },
          {
            label: t('bookingForm.review.building'),
            value: selectedBuilding?.buildingName ?? val('buildingId'),
          },
          {
            label: t('bookingForm.review.attendees'),
            value:
              typeof formData.numberOfAttendees === 'number'
                ? String(formData.numberOfAttendees)
                : '—',
          },
        ]}
        onEdit={() => onEditStep(0)}
        sectionState={getSectionState(0, highestVisitedStep, stepCompletionMap)}
        loading={buildingLoading}
      />

      {/* 3. Event details */}
      <ReviewSection
        title={t('bookingForm.review.eventDetailsTitle')}
        icon={<DocumentRegular />}
        columns={3}
        items={[
          { label: t('bookingForm.review.meetingName'), value: val('eventTitle') },
          { label: t('bookingForm.review.attendeeType'), value: val('attendeeTypeCode') },
          { label: t('bookingForm.review.msFteSponsor'), value: val('sponsor.name') },
          { label: t('bookingForm.review.visitPurpose'), value: visitPurposeLabel },
          ...(showBusinessGroup
            ? [{ label: t('bookingForm.review.businessGroup'), value: businessGroupLabel }]
            : []),
          ...(showBusinessGroup
            ? [
                {
                  label: t('bookingForm.review.businessJustification'),
                  value: val('businessJustification'),
                },
              ]
            : []),
          {
            label: t('bookingForm.review.attendees'),
            value:
              typeof formData.numberOfAttendees === 'number'
                ? String(formData.numberOfAttendees)
                : '—',
          },
          ...(val('sponsor.costCenter') !== '—'
            ? [{ label: t('bookingForm.review.ioCcNumber'), value: val('sponsor.costCenter') }]
            : []),
        ]}
        onEdit={() => onEditStep(2)}
        sectionState={getSectionState(2, highestVisitedStep, stepCompletionMap)}
      >
        {contacts.length > 0 && (
          <>
            <div className={styles.subSectionDivider} />
            <Text className={styles.subSectionTitle}>
              {t('bookingForm.review.onSiteContactsLabel')}
            </Text>
            <div className={styles.contactsGrid}>
              {contacts.map((c, i) => (
                <div key={i} className={styles.contactItem}>
                  <Text className={styles.contactName}>{c.name}</Text>
                  <Text className={styles.contactEmail}>{c.email}</Text>
                </div>
              ))}
            </div>
          </>
        )}
      </ReviewSection>

      {/* 4. Additional details */}
      <ReviewSection
        title={t('bookingForm.review.additionalDetailsTitle')}
        icon={<SettingsRegular />}
        columns={2}
        items={[
          { label: t('bookingForm.review.cateringRequired'), value: yesNo('hasCatering') },
          { label: t('bookingForm.review.eventFilmed'), value: yesNo('filmingRequired') },
          {
            label: t('bookingForm.review.overnightStorage'),
            value: yesNo('overnightStorageRequired'),
          },
          { label: t('bookingForm.review.anyShipments'), value: yesNo('shipmentsExpected') },
        ]}
        onEdit={() => onEditStep(3)}
        sectionState={getSectionState(3, highestVisitedStep, stepCompletionMap)}
      >
        {Boolean(formData.additionalInformation) && (
          <>
            <div className={styles.subSectionDivider} />
            <Text className={styles.subSectionTitle}>
              {t('bookingForm.review.additionalDetailsLabel')}
            </Text>
            <Text className={styles.additionalText}>{val('additionalInformation')}</Text>
          </>
        )}
        {formData.hasCatering === 'yes' && selectedBuilding?.caterers && (
          <CaterersPanel caterers={selectedBuilding.caterers} />
        )}
        {formData.filmingRequired === 'yes' && selectedBuilding && (
          <FilmingApprovalPanel
            filmingFormUrl={selectedBuilding.filmingFormUrl}
            gsocEmail={selectedBuilding.gsocEmail}
            regionalSecurityAdvisorEmail={selectedBuilding.regionalSecurityAdvisorEmail}
            siteContactEmail={selectedBuilding.siteContactEmail}
          />
        )}
      </ReviewSection>

      {/* Acknowledgement Banner — only shown after user checks the policy checkbox */}
      {!!formData.policyAcknowledgement && (
        <div className={styles.acknowledgementBanner}>
          <CheckmarkCircleRegular className={styles.acknowledgementIcon} />
          <Text className={styles.acknowledgementText}>
            {t('bookingForm.review.acknowledgementBanner')}
          </Text>
        </div>
      )}
    </div>
  );
};

ReviewSummary.displayName = 'ReviewSummary';
